<template>
  <DiceRollerPopover :visible="showDiceRoller" @remove-inspiration="removeInspiration" @close="showDiceRoller = false" />

  <Teleport to="#navbarEnd">
    <div class="headerButtons">
      <div :class="{ 'buttons': true, 'dropdown': true, 'is-active': menuOpen }">
        <div class="dropdown-trigger">
          <button class="button menuButton" @click="menuOpen = !menuOpen"></button>
        </div>
        <div class="dropdown-menu">
          <div class="dropdown-content">
            <label class="dropdown-item checkbox">
              <input type="checkbox" :checked="appState.hardRolls" @input="toggleHardRolls" />
              Roll physical dice
            </label>
            <template v-if="joinState.status === OFFLINE">
              <hr class="dropdown-divider">
              <a class="dropdown-item" @click="openJoinRoomModal">
                Join Room
              </a>
            </template>
          </div>
        </div>
      </div>
      <div class="buttons">
        <button class="button" @click="openModal('manageEffects')">Bonuses</button>
      </div>
      <div class="buttons has-addons">
        <button class="button" @click="openModal('shortRest')">Short Rest</button>
        <button class="button" @click="openModal('longRest')">Long Rest</button>
      </div>
      <div class="buttons">
        <button class="button is-primary" @click="showDiceRoller = true">Roll Dice</button>
      </div>
    </div>
  </Teleport>

  <div data-view="playCharacter">
    <div class="columns is-variable is-6">
      <div class="column is-one-third-widescreen is-half-desktop is-one-quarter-fullhd">
        <Character :character="character" :computed="computed" @inspired="toggleInspiration" />
      </div>

      <div class="column">
        <!-- Features -->
        <section v-if="featureCollection.length" class="block" id="featuresSection">
          <p :class="{ 'label sectionLabel': true, 'isCollapsed': sectionCollapsed.features }" @click="sectionCollapsed.features = !sectionCollapsed.features">Features</p>
          <div class="content block">
            <CardGroup>
              <template v-for="card in featureCollection" :key="card">
                <FeatureCard v-if="allFeatures[card].type === 'feature'" :feature="card" />
              </template>
            </CardGroup>
          </div>
        </section>

        <!-- Actions -->
        <section v-if="actionCollection.length" class="block" id="actionsSection">
         <p :class="{ 'label sectionLabel': true, 'isCollapsed': sectionCollapsed.actions }" @click="sectionCollapsed.actions = !sectionCollapsed.actions">Actions</p>
          <div class="content block">
            <CardGroup>
              <template v-for="card in actionCollection" :key="card">
                <template v-if="allFeatures[card]">
                  <ActionCard
                    v-if="allFeatures[card].roll || allFeatures[card].bonus?.toggle || allFeatures[card].requiresRested"
                    check-icon="active"
                    :feature="card"
                    :clickable="!allFeatures[card].bonus?.toggle"
                    :checkbox="allFeatures[card].bonus?.toggle"
                    :value="character.activeEffects.includes(card)"
                    :disabled="allFeatures[card].requiresRested && character.restedAbilitiesUsed[card]"
                    @click="() => !allFeatures[card].bonus?.toggle && openActionModal(card, allFeatures[card])"
                    @changed="toggleFeatureBonus($event, card)"
                  />
                  <ActionCard v-else :feature="card" />
                </template>
                <template v-else>
                  <ActionCard
                    v-if="allActions[card].roll || allActions[card].requiresRested"
                    check-icon="active"
                    :action="card"
                    :clickable="true"
                    :value="character.activeEffects.includes(card)"
                    :disabled="allActions[card].requiresRested && character.restedAbilitiesUsed[card]"
                    @click="openActionModal(card, allActions[card])"
                  />
                  <ActionCard v-else :action="card" />
                </template>
              </template>
            </CardGroup>
          </div>
        </section>

        <!-- Spells -->
        <section v-if="spellCollection.length" class="block" id="spellsSection">
          <p :class="{ 'label sectionLabel': true, 'isCollapsed': sectionCollapsed.spells }" @click="sectionCollapsed.spells = !sectionCollapsed.spells">Spells</p>
          <div class="content block">
            <CardGroup>
              <template v-for="card in spellCollection" :key="card">
                <SpellCard
                  v-if="allSpells[card].damage || allSpells[card].bonus || allSpells[card].roll"
                  :check-icon="allSpells[card].bonus?.concentrate ? 'concentrate' : 'active'"
                  :spell="card"
                  :clickable="true"
                  :value="character.activeEffects.includes(card)"
                  :disabled="!character.activeEffects.includes(card) && (character.restedAbilitiesUsed[card] || (character.spellPoints < allSpells[card].level))"
                  @click="openSpellModal(card, allSpells[card])"
                />
                <SpellCard v-else :spell="card" />
              </template>
            </CardGroup>
          </div>
        </section>

        <!-- Weapons -->
        <section v-if="weaponCollection.length" class="block" id="weaponsSection">
          <p :class="{ 'label sectionLabel': true, 'isCollapsed': sectionCollapsed.weapons }" @click="sectionCollapsed.weapons = !sectionCollapsed.weapons">Weapons</p>
          <div class="content block">
            <CardGroup>
              <template v-for="card in weaponCollection" :key="card">
                <EquipmentCard
                  v-if="allWeapons[card].armor"
                  :equipment="card"
                  :checkbox="true"
                  :true-value="card"
                  :value="character.equippedArmor"
                  @changed="toggleEquipped($event, card, allWeapons[card])"
                />
                <WeaponCard 
                  v-else 
                  :weapon="card"
                  :clickable="true"
                  @click="openModal('attackWeapon', { key: card, weapon: allWeapons[card] })"
                />
              </template>
            </CardGroup>
          </div>
          <!-- <div class="block">
            <button class="button">Add Weapon</button>
          </div> -->
        </section>

        <!-- Equipment -->
        <section v-if="equipmentCollection.length" class="block" id="equipmentSection">
          <p :class="{ 'label sectionLabel': true, 'isCollapsed': sectionCollapsed.equipment }" @click="sectionCollapsed.equipment = !sectionCollapsed.equipment">Equipment</p>
          <div class="content block">
            <CardGroup>
              <template v-for="card in equipmentCollection" :key="card">
                <!-- Equippable armor -->
                <EquipmentCard
                  v-if="allEquipment[card].armor"
                  :equipment="card"
                  :checkbox="true"
                  :true-value="card"
                  :value="character.equippedArmor"
                  @changed="toggleEquipped($event, card, allEquipment[card])"
                />
                <!-- Useable -->
                <EquipmentCard
                  v-else-if="allEquipment[card].consumable || allEquipment[card].roll || allEquipment[card].bonus || allEquipment[card].requiresRested"
                  check-icon="active"
                  :equipment="card"
                  :clickable="true"
                  :value="character.activeEffects.includes(card)"
                  :disabled="(allEquipment[card].requiresRested && character.restedAbilitiesUsed[card]) || (!character.inventory[card])"
                  @click="openEquipmentModal(card, allEquipment[card])"
                />
                <!-- Everything else -->
                <EquipmentCard v-else :equipment="card" />
              </template>
            </CardGroup>
          </div>
          <!-- <div class="block">
            <button class="button">Add Equipment</button>
          </div> -->
        </section>
      </div>
    </div>
  </div>

  <Teleport to="#modalContent">
    <!-- Modify health -->
    <Modal name="modifyHealth">
      <template v-slot:default="{ options }">
        <ModalCard>
          <template v-slot:title>Modify Health</template>
          
          <div class="field has-addons has-addons-centered">
            <div class="control">
              <input ref="modifyHealthAmountEl" class="input isShort" type="number" placeholder="0" :value="options.value || 0" :disabled="options.value" :min="0" :max="maxHealth">
            </div>
            <div class="control">
              <div class="button is-primary" @click="addHealth">
                Add
              </div>
            </div>
            <div class="control">
              <div class="button is-primary" @click="subtractHealth">
                Subtract
              </div>
            </div>
            <div v-if="options.value === undefined" class="control">
              <div class="button is-text" @click="setHealth">
                Set
              </div>
            </div>
          </div>
        </ModalCard>
      </template>
    </Modal>

    <!-- Modify spell points -->
    <Modal name="modifySpellPoints">
      <template v-slot:default="{ options }">
        <ModalCard>
          <template v-slot:title>Modify Spell Points</template>
          
          <div class="field has-addons has-addons-centered">
            <div class="control">
              <input ref="modifySpellPointsAmountEl" class="input isShort" type="number" placeholder="0" :value="options.value || 0" :disabled="options.value" :min="0" :max="characterClass.spellPoints">
            </div>
            <div class="control">
              <div class="button is-primary" @click="addSubtractSpellPoints">
                Spend Points
              </div>
            </div>
            <div v-if="options.value === undefined" class="control">
              <div class="button is-text" @click="setSpellPoints">
                Set
              </div>
            </div>
          </div>
        </ModalCard>
      </template>
    </Modal>

    <!-- Short rest -->
    <Modal name="shortRest">
      <ModalCard>
        <template v-slot:title>Short Rest</template>
        
        <div class="field has-addons has-addons-centered">
          <div class="control">
            <p>
              Take a short rest of at least one hour, doing nothing more strenuous than eating, drinking, reading, and tending to wounds.
            </p>
            <p>Taking a short rest will...</p>
            <ul>
              <li>Restore {{characterClass.healAmount}} health</li>
              <li>Reset your rested abilities</li>
              <li>Remove applied effects with a short duration</li>
            </ul>
          </div>
        </div>

        <template v-slot:buttons>
          <div class="button is-primary" @click="shortRest">
            Take Short Rest
          </div>
        </template>
      </ModalCard>
    </Modal>

    <!-- Long rest -->
    <Modal name="longRest">
      <ModalCard>
        <template v-slot:title>Long Rest</template>
        
        <div class="field has-addons has-addons-centered">
          <div class="control">
            <p>
              Take an extended rest of at least eight hours, during which you may sleep or perform light activities such as reading, talking, eating, or standing watch.
            </p>
            <p>Taking a long rest will...</p>
            <ul>
              <li>Restore all of your health</li>
              <li>Restore all of your spell points</li>
              <li>Reset your rested abilities</li>
              <li>Remove all applied effects</li>
            </ul>
          </div>
        </div>

        <template v-slot:buttons>
          <div class="button is-primary" @click="longRest">
            Take Long Rest
          </div>
        </template>
      </ModalCard>
    </Modal>
    
    <!-- Track rested -->
    <Modal name="trackRested">
      <template v-slot:default="{ options }">
        <ModalCard>
          <template v-slot:title>Track Rested Ability</template>
          
          <div class="field">
            <div class="control has-text-centered">
              Track <strong>{{ options.item.name }}</strong> as used?
            </div>
          </div>

          <template v-slot:buttons>
            <div class="button is-primary" @click="terminateTrackRestedModal(options)">
              Track as Used
            </div>
          </template>
        </ModalCard>
      </template>
    </Modal>
    
    <!-- Activate effect -->
    <Modal name="activateEffect">
      <template v-slot:default="{ options }">
        <ModalCard>
          <template v-slot:title>{{ character.activeEffects.includes(options.key) ? 'Remove' : 'Apply' }} Effect</template>
          
          <div class="field">
            <div class="control has-text-centered">
              {{ character.activeEffects.includes(options.key) ? 'Remove' : 'Apply' }} the effects of <strong>{{ options.item.name }}</strong>?
            </div>
          </div>

          <template v-slot:buttons>
            <div class="button is-primary" @click="terminateActivateEffectModal(options)">
              {{ character.activeEffects.includes(options.key) ? 'Remove' : 'Apply' }} Effect
            </div>
          </template>
        </ModalCard>
      </template>
    </Modal>
    
    <!-- Manage effects -->
    <Modal name="manageEffects">
      <ModalCard>
        <template v-slot:title>Bonuses</template>
        
        <div class="table-container" style="font-size:90%;max-height:60vh;overflow-y:auto">
          <table class="table">
            <tbody>
              <template v-for="bonusDef in filteredBonuses" :key="bonusDef.name">
                <tr>
                  <th>{{ bonusDef.name }}</th>
                  <td>{{ plusOrMinus(bonusDef.value) }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- <div class="field has-addons has-addons-centered">
          <div class="control">
            <div class="select">
              <select name="country">
                <option value="Argentina">Argentina</option>
              </select>
            </div>
          </div>
          <div class="control">
            <button type="submit" class="button is-primary">Apply Effect</button>
          </div>
        </div> -->
      </ModalCard>
    </Modal>
    
    <!-- Consume equipment -->
    <Modal name="consumeEquipment">
      <template v-slot:default="{ options }">
        <ModalCard>
          <template v-slot:title>Use Equipment</template>
          
          <div class="field">
            <div class="control has-text-centered">
              Do you want to use one <strong>{{ options.item.name }}</strong>?
            </div>
          </div>

          <template v-slot:buttons>
            <div class="button is-primary" @click="terminateModal(() => consumeEquipment(options.key), options)">
              Use Equipment
            </div>
          </template>
        </ModalCard>
      </template>
    </Modal>
    
    <!-- Edit notes -->
    <EditNotesModal @notes-updated="updateNotes" />

    <!-- Attack with weapon -->
    <AttackWeaponModal @remove-inspiration="removeInspiration" />
    <DamageWeaponModal @remove-inspiration="removeInspiration" />

    <!-- Attack with spell -->
    <AttackSpellModal @remove-inspiration="removeInspiration" />
    <DamageSpellModal @remove-inspiration="removeInspiration" />

    <!-- Ability checks/Saving Throws -->
    <AbilityCheckModal @remove-inspiration="removeInspiration" />

    <!-- Roll dice -->
    <RollDiceModal @remove-inspiration="removeInspiration" @apply-health="applyHealth" />

    <!-- Join room -->
    <Modal name="joinRoom">
      <ModalCard :hide-close="joinState.status !== JOINED && joinState.status !== OFFLINE">
        <template v-slot:title>Join a Room</template>
        
        <template v-if="joinState.status === OFFLINE">
          <div class="field">
            <div class="control has-text-centered">
              <input type="text" class="input is-large" v-model="joinCode" placeholder="Room code" style="text-align: center" />
            </div>
          </div>
          <div class="field">
            <div class="control has-text-centered">
              Enter a room code to join an online game.
            </div>
          </div>
        </template>
        <template v-if="joinState.status === CONNECTING">
          <div class="field">
            <div class="control has-text-centered">
              Connecting to server...
            </div>
          </div>
        </template>
        <template v-if="joinState.status === JOINING_ROOM">
          <div class="field">
            <div class="control has-text-centered">
              Joining room...
            </div>
          </div>
        </template>
        <template v-if="joinState.status === JOINED">
          <div class="field">
            <div class="control has-text-centered roomCode">
              <span class="title is-3">Joined {{ joinState.code }}</span>
            </div>
          </div>
          <div class="field">
            <div class="control has-text-centered">
              You've joined your friend's room! Close this window to start playing.
            </div>
          </div>
        </template>

        <template v-slot:buttons>
          <button v-if="joinState.status === OFFLINE" class="button is-primary" @click="doJoinRoom" :disabled="!joinCode">Join Room</button>
          <button v-else-if="joinState.status === CONNECTING || joinState.status === JOINING_ROOM" class="button is-primary is-loading" :disabled="true">Join Room</button>
        </template>
      </ModalCard>
    </Modal>

    <button class="button is-primary modalRollDiceButton" @click="showDiceRoller = true">Roll Dice</button>
  </Teleport>
</template>

<script>
/* eslint-disable */
import { defineComponent, computed, provide, ref, inject, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'

// Hooks & helpers
import characterComputedValues from '@/hooks/characterComputedValues'
import useCharacters from '@/hooks/useCharacters'
import useModal from '@/hooks/useModal'
import plusOrMinus from '@/helpers/plusOrMinus'
import useToast from '@/hooks/useToast'
import { useJoin, OFFLINE, JOINED, CONNECTING, JOINING_ROOM } from '@/hooks/room'

// Components
import Character from '@/components/Character'
import CardGroup from '@/components/CardGroup'
import FeatureCard from '@/components/cards/FeatureCard'
import ActionCard from '@/components/cards/ActionCard'
import WeaponCard from '@/components/cards/WeaponCard'
import EquipmentCard from '@/components/cards/EquipmentCard'
import SpellCard from '@/components/cards/SpellCard'
import Modal from '@/components/Modal'
import ModalCard from '@/components/ModalCard'
import DiceRollerPopover from '@/components/DiceRollerPopover'

// Modals
import AttackWeaponModal from '@/components/modals/AttackWeapon'
import DamageWeaponModal from '@/components/modals/DamageWeapon'
import AttackSpellModal from '@/components/modals/AttackSpell'
import DamageSpellModal from '@/components/modals/DamageSpell'
import AbilityCheckModal from '@/components/modals/AbilityCheck'
import RollDiceModal from '@/components/modals/RollDice'
import EditNotesModal from '@/components/modals/EditNotes'

// Data
import allActions from '@/data/actions'
// import allClass from '@/data/classes'
import allFeatures from '@/data/features'
// import allRaces from '@/data/races'
// import allSkills from '@/data/skills'
import allWeapons from '@/data/weapons'
// import allDifficulties from '@/data/difficulties'
import allEquipment from '@/data/equipment'
import allSpells from '@/data/spells'
import allBonuses from '@/data/bonuses'

export default defineComponent({
  components: {
    Character,
    CardGroup,
    FeatureCard,
    ActionCard,
    WeaponCard,
    EquipmentCard,
    SpellCard,
    Modal,
    ModalCard,
    AttackWeaponModal,
    DamageWeaponModal,
    AttackSpellModal,
    DamageSpellModal,
    AbilityCheckModal,
    RollDiceModal,
    EditNotesModal,
    DiceRollerPopover
  },

  setup () {
    // State

    const route = useRoute()
    const { openModal, closeModal, currentModal } = useModal()
    const toast = useToast()
    const { characters, save: saveCharacters } = useCharacters()
    const { 
      state: joinState,
      connect: connectJoin, 
      joinRoom, 
      // roll: sendRoll, 
      updateCharacter,
      on: addJoinEventHandler,
      off: removeJoinEventHandler
    } = useJoin()

    const modifyHealthAmountEl = ref(null)
    const modifySpellPointsAmountEl = ref(null)

    const showDiceRoller = ref(false)
    const menuOpen = ref(false)

    const appState = inject('appState')

    // Character

    const character = computed(() => characters.find((c) => c.id === route.params.id))
    provide('character', character.value)

    const computedCharacter = characterComputedValues(character.value)
    provide('computedCharacter', computedCharacter)

    const save = () => {
      saveCharacters()
      if (joinState.status === JOINED) {
        updateCharacter(character.value)
      }
    }

    // Collections

    // TODO: put features with toggles as actions and dont show in features

    const featureCollection = computed(() => ([
      ...character.value.features,
      ...(computedCharacter.race.value.features || []),
      ...(computedCharacter.characterClass.value.features || [])
    ].sort()))

    const actionCollection = computed(() => ([
      ...Object.keys(allActions),
      ...featureCollection.value.filter((key) => allFeatures[key].type !== 'feature')
    ].sort()))

    const spellCollection = computed(() => ([
      ...character.value.spells,
      ...(computedCharacter.race.value.spells || []),
      ...(computedCharacter.characterClass.value.spells || [])
    ].sort()))

    const weaponCollection = computed(() => ([
      ...character.value.weapons
    ].sort()))

    const equipmentCollection = computed(() => ([
      ...character.value.equipment
    ].sort()))

    // Methods

    const clampHealth = (amount) => Math.min(Math.max(amount, 0), computedCharacter.maxHealth.value)
    const addHealth = () => {
      const amount = Number(modifyHealthAmountEl.value.value) || 0
      character.value.health = clampHealth(character.value.health + amount)

      closeModal()
      save()
      toast(`Restored ${amount} health.`)
    }
    const subtractHealth = () => {
      const amount = (Number(modifyHealthAmountEl.value.value) || 0) * -1
      character.value.health = clampHealth(character.value.health + amount)

      closeModal()
      save()
      toast(`Removed ${amount} health.`)
    }
    const setHealth = () => {
      const amount = Number(modifyHealthAmountEl.value.value) || 0
      character.value.health = clampHealth(amount)

      closeModal()
      save()
      toast(`Health set to ${amount}.`)
    }

    const clampSpellPoints = (amount) => Math.min(Math.max(amount, 0), computedCharacter.characterClass.value.spellPoints)
    const addSubtractSpellPoints = () => {
      const amount = Number(modifySpellPointsAmountEl.value.value) || 0
      character.value.spellPoints = clampSpellPoints(character.value.spellPoints - amount)

      closeModal()
      save()
      toast(`Spent ${amount} spell points.`)
    }
    const setSpellPoints = () => {
      const amount = Number(modifySpellPointsAmountEl.value.value) || 0
      character.value.spellPoints = clampSpellPoints(amount)

      closeModal()
      save()
      toast(`Spell points set to ${amount}.`)
    }

    const shortRest = () => {
      character.value.health = clampHealth(character.value.health + computedCharacter.characterClass.value.healAmount)
      character.value.restedAbilitiesUsed = {}
      character.value.activeEffects = character.value.activeEffects.filter((key) => {
        const item = allSpells[key] || allEquipment[key] || allFeatures[key] || allActions[key]
        if (item.bonus?.duration === 'short') return false
        return true
      })
      closeModal()
      save()
      toast(`You feel rested.`)
    }
    const longRest = () => {
      character.value.health = computedCharacter.maxHealth.value
      character.value.spellPoints = computedCharacter.characterClass.value.spellPoints
      character.value.restedAbilitiesUsed = {}
      character.value.activeEffects = []

      closeModal()
      save()
      toast(`You feel well rested.`)
    }

    const updateNotes = (notes) => {
      character.value.notes = notes
      closeModal()
      save()
      toast(`Notes updated.`)
    }

    const toggleEquipped = (add, key, item) => {
      if (add) {
        character.value.equippedArmor.push(key)
        toast(`Equipped ${item.name}.`)
      } else {
        const index = character.value.equippedArmor.indexOf(key)
        character.value.equippedArmor.splice(index, 1)
        toast(`Unequipped ${item.name}.`)
      }
      save()
    }

    const toggleInspiration = (enabled) => {
      const key = 'inspiration'
      if (enabled) {
        if (!character.value.activeEffects.includes(key)) character.value.activeEffects.push(key)
        toast(`You feel inspired.`)
      } else {
        const index = character.value.activeEffects.indexOf(key)
        if (index >= 0) character.value.activeEffects.splice(index, 1)
        toast(`Inspiration has left you.`)
      }
      save()
    }

    const removeInspiration = () => {
      const index = character.value.activeEffects.indexOf('inspiration')
      if (index >= 0) character.value.activeEffects.splice(index, 1)
      toast(`Inspiration has left you.`)
      save()
    }

    const consumeEquipment = (key) => {
      const count = character.value.inventory[key]
      if (count - 1 === 0) {
        if (allEquipment[key].charges) {
          character.value.inventory[key] = 0
          toast(`Used the last charge of ${allEquipment[key].name}.`)
        } else {
          delete character.value.inventory[key]
          const index = character.value.equipment.indexOf(key)
          character.value.equipment.splice(index, 1)
          toast(`Used the last ${allEquipment[key].name}.`)
        }
      } else {
        character.value.inventory[key]--
        if (allEquipment[key].charges) {
          toast(`Used one charge of ${allEquipment[key].name}.`)
        } else {
          toast(`Used one ${allEquipment[key].name}.`)
        }
      }
      save()
    }

    const applyHealth = (amount) => {
      character.value.health = clampHealth(character.value.health + amount)
      save()
      toast(`Restored ${amount} health.`)
    }

    const applySpellPoints = (amount) => {
      character.value.spellPoints = clampSpellPoints(character.value.spellPoints - amount)
      save()
      toast(`Spent ${amount} spell points.`)
    }

    // Modal methods

    const trackRested = (key, item) => {
      character.value.restedAbilitiesUsed[key] = true
      toast(`Exhausted use of ${item.name}.`)
    }

    const activateEffect = (key, item) => {
      if (character.value.activeEffects.includes(key)) {
        const index = character.value.activeEffects.indexOf(key)
        character.value.activeEffects.splice(index, 1)
        toast(`Removed effects of ${item.name}.`)
      } else {
        character.value.activeEffects.push(key)
        toast(`Applied effects of ${item.name}.`)
      }
    }

    const openEquipmentModal = (key, item) => {
      const options = { key, item, stack: [] }

      let modalName
      if (item.consumable) {
        modalName = 'consumeEquipment'
        if (item.roll) options.stack.push({ name: 'rollDice' })
      } else if (item.roll) {
        modalName = 'rollDice'
      } else if (item.bonus) {
        modalName = 'activateEffect'
      } else if (item.requiresRested) {
        modalName = 'trackRested'
      }
      
      openModal(modalName, options)
    }

    const openActionModal = (key, item) => {
      const options = { key, item, stack: [] }

      let modalName
      if (item.roll) {
        modalName = 'rollDice'
      } else if (item.bonus) {
        modalName = 'activateEffect'
      } else if (item.requiresRested) {
        modalName = 'trackRested'
      }

      openModal(modalName, options)
    }

    const openSpellModal = (key, item) => {
      const options = { key, item, stack: [] }

      let modalName
      if (item.damage) {
        modalName = 'attackSpell'
        if (item.roll) {
          options.stack.push({ name: 'rollDice' })
        } else {
          options.stack.push({ name: 'damageSpell' })
        }
      } else {
        if (!character.value.activeEffects.includes(key)) {
          options.spellPointsSpent = item.level
        }
        if (item.roll) {
          modalName = 'rollDice'
        } else if (item.bonus) {
          modalName = 'activateEffect'
        } else if (item.requiresRested) {
          modalName = 'trackRested'
        }
      }

      openModal(modalName, options)
    }

    const terminateModal = (fn, options) => {
      if (fn) fn()

      if (!options.stack || !options.stack.length) {
        const { key, item } = options
        if (item.bonus) activateEffect(key, item)
        if (item.requiresRested) trackRested(key, item)
        if (options.spellPointsSpent) applySpellPoints(options.spellPointsSpent)
        save()
      } else if (options.stack) {
        const next = options.stack.shift()
        openModal(next.name, options)
        return
      }

      closeModal()
    }
    provide('terminateModal', terminateModal)

    const terminateActivateEffectModal = (options) => {
      terminateModal(null, options)
      save()
    }

    const terminateTrackRestedModal = (options) => {
      terminateModal(null, options)
      save()
    }

    // Menu options

    const toggleHardRolls = () => {
      appState.hardRolls = !appState.hardRolls
      menuOpen.value = false
      if (appState.hardRolls) toast('Using physical dice.')
      else toast('Using dice roller.')
    }

    const joinCode = ref('')

    watch(currentModal, () => joinCode.value = '')

    const openJoinRoomModal = () => {
      openModal('joinRoom')
      menuOpen.value = false
    }

    const doJoinRoom = async () => {
      try {
        await connectJoin()
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.warn(err)
        return
      }
      joinRoom(joinCode.value, character.value)
      currentModal.value.options.hideClose = true
    }

    const giveInspirationHandler = () => {
      if (!character.value.activeEffects.includes('inspiration')) {
        character.value.activeEffects.push('inspiration')
        toast(`You feel inspired.`)
      }
      saveCharacters()
    }

    const errorHandler = () => {
      joinCode.value = ''
    }

    onMounted(() => {
      addJoinEventHandler('giveInspiration', giveInspirationHandler)
      addJoinEventHandler('error', errorHandler)
    })

    onUnmounted(() => {
      removeJoinEventHandler(giveInspirationHandler)
      removeJoinEventHandler(errorHandler)
    })

    const filteredBonuses = computed(() => Object
      .keys(computedCharacter.bonuses.value)
      .map((key) => ({
        name: allBonuses[key].name,
        value: computedCharacter.bonuses.value[key]
      }))
      .filter((bonus) => !!bonus.value && bonus.name !== "")
    )

    // Give equipment
    const giveEquipmentHandler = ({ itemId, qty }) => {
      const item = allEquipment[itemId]
      if (!character.value.equipment.includes(itemId)) character.value.equipment.push(itemId)
      if (item.consumable) {
        if (!character.value.inventory[itemId]) character.value.inventory[itemId] = qty
        else character.value.inventory[itemId] += qty
      }
      toast(`${qty} ${item.name} added to your inventory.`)
      saveCharacters()
    }
    onMounted(() => {
      addJoinEventHandler('giveEquipment', giveEquipmentHandler)
    })
    onUnmounted(() => {
      removeJoinEventHandler(giveEquipmentHandler)
    })

    const toggleFeatureBonus = (trueValue, key) => {
      activateEffect(key, allFeatures[key])
      save()
    }

    const sectionCollapsed = ref({
      features: false,
      actions: false,
      weapons: false,
      equipment: false,
      spells: false
    })

    return {
      appState,
      showDiceRoller,
      menuOpen,
      joinState,
      joinCode,
      OFFLINE,
      JOINED,
      CONNECTING,
      JOINING_ROOM,
      sectionCollapsed,

      // Modals
      openModal,
      openEquipmentModal,
      openActionModal,
      openSpellModal,
      terminateModal,
      terminateActivateEffectModal,
      terminateTrackRestedModal,

      // Methods
      plusOrMinus,
      addHealth,
      subtractHealth,
      setHealth,
      shortRest,
      longRest,
      toggleEquipped,
      addSubtractSpellPoints,
      setSpellPoints,
      trackRested,
      activateEffect,
      consumeEquipment,
      updateNotes,
      applyHealth,
      toggleInspiration,
      removeInspiration,
      toggleHardRolls,
      openJoinRoomModal,
      doJoinRoom,
      toggleFeatureBonus,

      // Els,
      modifyHealthAmountEl,
      modifySpellPointsAmountEl,

      // Character
      character,
      computed: computedCharacter,
      ...computedCharacter,
      actionCollection,
      featureCollection,
      spellCollection,
      weaponCollection,
      equipmentCollection,

      // Data
      allActions,
      allFeatures,
      allEquipment,
      allWeapons,
      allSpells,
      allBonuses,
      filteredBonuses
    }
  }
})
</script>

<style lang="sass" scoped>
.headerButtons
  display: flex
  .buttons
    margin: 0 0.25rem !important
    .button
      margin-bottom: 0 !important

.menuButton::after
  content: '\e5d2'
  font-family: 'Material Icons'

.roomCode
  user-select: text
  cursor: pointer
  padding: 1rem
  background-color: rgba(#000, 0.05)
  font-family: 'Playfair Display'

.sectionLabel
  margin-bottom: 1.5rem
  display: flex
  cursor: pointer
  user-select: none
  &::before
    content: '\e5d6'
    display: flex
    margin-right: 0.1rem
    width: 1.5rem
    height: 1.5rem
    align-items: center
    justify-content: center
    color: #7a7a7a
    font-size: 120%
    font-family: 'Material Icons'
  &.isCollapsed
    &::before
      content: '\e5d7'
    + .content.block
      display: none

.modalRollDiceButton
  position: fixed
  top: 0.5rem
  right: 1rem
  z-index: 999
</style>
